<template>
  <div
    v-if="bulletin"
    :class="'bulletin_section bulletin_section--' + bulletin.bulletin_category_slug"
  >
    <div class="main-content full-width-inside-wrapper">
      <div class="row">
        <div class="col-lg-12">
          <div class="row">
            <div class="col-lg-12 bulletin content">
              <div
                class="bulletin-content content-area content-area--single post type-post status-publish format-standard hentry category-news"
                v-html="bulletin.content"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Paragraph from "@/design-system/elements/Paragraph.vue";
import { CHeading, CText } from "@chakra-ui/vue";
import { IBulletin } from "@iloq/shared";
import { defineComponent } from "vue";

export default defineComponent({
  name: "Bulletin",
  components: {
    Paragraph,
    CText,
    CHeading,
  },

  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data(): any {
    return {
      bulletin: {},
    };
  },
  mounted(): void {
    this.fetchBulletin();
  },
  watch: {
    id() {
      this.fetchBulletin();
    },
  },
  methods: {
    fetchBulletin(): void {
      const bulletinId = this.id;
      this.bulletin = this.$store.state.bulletin.all.find(
        (element: IBulletin) => element.id === bulletinId
      );

      if (!this.bulletin) {
        // Happens when user comes with direct link (& state is not initialized)
        this.$store.watch(
          (state: any) =>
            state.bulletin.all.find((element: IBulletin) => element.id === bulletinId),
          (newValue: IBulletin) => {
            if (newValue) {
              this.bulletin = newValue;
            }
          }
        );
      }
    },
  },
});
</script>

<style lang="scss">
div#app.bulletin .wrapper {
  overflow-x: scroll;
  background-color: #f4f4f4;
}
</style>

<style lang="scss" scoped>
@import "../design-system/assets/tokens/tokens";

.bulletin_section {
  .bulletin_header {
    background: #ccc;
    padding: 0 60px;
    .bulletin-date,
    .bulletin-category {
      font-size: 28px;
      font-weight: bold;
      color: $color-iloq-blue-dark;
    }
    // HEADS-UP
    &.bulletin_header--headsUp {
      background: #f5f9cc;
    }
    // LOCAL NEWS
    &.bulletin_header--localNews {
      background: $color-iloq-blue-light;
      .bulletin-date,
      .bulletin-category {
        color: white;
      }
    }
    // EVENTS
    &.bulletin_header--events {
      background: $color-iloq-green;
      .bulletin-date,
      .bulletin-category {
        color: white;
      }
    }
  }
  ::v-deep .bulletin {
    /*
    The process for collecting&creating these styles was as follows:
    - Open the example article from iloq.com in Chrome
    - Using dev tools, remove all surrounding extra stuff (header, footer etc.)
    - Using Chrome extension "CSS Used", copy styles used by the body element
    - Paste the styles here and auto-fix eslint issues
    - Remove lots of unused styles (used by js-related animations etc. that are not available in Partner Portal)
    - Tweak the styles to make the example content fit better to Partner Portal
      - Re-target some body styles to .bulletin-content
      - Make some elements narrower so they fit to the window even when the sidebar is open
      - Scale down things (make less airy)
      - Note: All tweaks have comments
     */
    /* Added variable for scaling down text & spacings. The default styles were too "airy" compared to other Partner Portal styles.
     NOTE: All calc(var(--scale-pp) * X) formulas below are tweaks added by Vincit */
    --scale-pp: 0.85;
    /* Variables copied from iloq.com */
    --site-max-width: 100%;
    --container-width: 92.9166666667rem;
    --container-width--medium: 73.8333333333rem;
    --container-width--small: 54.75rem;
    --space-3xs: calc(var(--scale-pp) * clamp(0.25rem, calc(0.2rem + 0.27vw), 0.63rem));
    --space-2xs: calc(var(--scale-pp) * clamp(0.5rem, calc(0.39rem + 0.54vw), 1.25rem));
    --space-xs: calc(var(--scale-pp) * clamp(0.75rem, calc(0.59rem + 0.8vw), 1.88rem));
    --space-s: calc(var(--scale-pp) * clamp(1rem, calc(0.79rem + 1.07vw), 2.5rem));
    --space-m: calc(var(--scale-pp) * clamp(1.5rem, calc(1.18rem + 1.61vw), 3.75rem));
    --space-l: calc(var(--scale-pp) * clamp(2rem, calc(1.57rem + 2.14vw), 5rem));
    --space-xl: calc(var(--scale-pp) * clamp(3rem, calc(2.36rem + 3.21vw), 7.5rem));
    --space-2xl: calc(var(--scale-pp) * clamp(3.75rem, calc(2.95rem + 4.02vw), 9.38rem));
    --space-3xl: calc(var(--scale-pp) * clamp(5rem, calc(3.93rem + 5.36vw), 12.5rem));
    --space-4xl: calc(var(--scale-pp) * clamp(6.75rem, calc(5.3rem + 7.23vw), 16.88rem));
    --space-3xs-2xs: calc(var(--scale-pp) * clamp(0.25rem, calc(0.11rem + 0.71vw), 1.25rem));
    --space-2xs-xs: calc(var(--scale-pp) * clamp(0.5rem, calc(0.3rem + 0.98vw), 1.88rem));
    --space-xs-s: calc(var(--scale-pp) * clamp(0.75rem, calc(0.5rem + 1.25vw), 2.5rem));
    --space-s-m: calc(var(--scale-pp) * clamp(1rem, calc(0.61rem + 1.96vw), 3.75rem));
    --space-m-l: calc(var(--scale-pp) * clamp(1.5rem, calc(1rem + 2.5vw), 5rem));
    --space-l-xl: calc(var(--scale-pp) * clamp(2rem, calc(1.21rem + 3.93vw), 7.5rem));
    --space-xl-2xl: calc(var(--scale-pp) * clamp(3rem, calc(2.09rem + 4.55vw), 9.38rem));
    --space-2xl-3xl: calc(var(--scale-pp) * clamp(3.75rem, calc(2.5rem + 6.25vw), 12.5rem));
    --space-3xl-4xl: calc(var(--scale-pp) * clamp(5rem, calc(3.3rem + 8.48vw), 16.88rem));
    --space-s-3xl: calc(var(--scale-pp) * clamp(1rem, calc(-0.64rem + 8.21vw), 12.5rem));
    --container-side-padding: calc(
      var(--scale-pp) * clamp(1.5rem, 0.8421052632rem + 2.6315789474vw, 4rem)
    );
    --color-white: #fff;
    --color-secondary-110: #1a1b31;
    --color-secondary-200: #e5dccf;
    --color-tertiary-110: #e6ff92;
    --color-grey-50: #f4f4f4;
    --color-grey-100: #ececec;
    --text-size-600: clamp(1.25rem, 1.0197368421rem + 0.9210526316vw, 2.125rem);
    --color-background: #f4f4f4;
    /*! CSS Used from: https://www.iloq.com/wp-content/themes/iloq-2021/build/theme.css?ver=1677094431 ; media=all */
    @media all {
      /* Tweaked by Vincit, was body */
      .bulletin-content {
        margin: 0;
      }
      main {
        display: block;
      }
      h1 {
        font-size: calc(var(--scale-pp) * 2em);
        margin: calc(var(--scale-pp) * 0.67em) 0;
      }
      a {
        background-color: transparent;
      }
      img {
        border-style: none;
      }
      *,
      :after,
      :before {
        box-sizing: inherit;
      }
      /* Tweaked by Vincit, was body */
      .bulletin-content {
        color: #3d3935;
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(1rem, 0.8684210526rem + 0.5263157895vw, 1.5rem));
        font-weight: 400;
        line-height: 1.5;
      }
      a {
        color: #0a485f;
        text-decoration: underline;
        transition: all 0.2s;
      }
      a:hover {
        text-decoration: none;
      }
      h1,
      h2,
      h3,
      h4 {
        margin: calc(var(--scale-pp) * 1.6em) 0 0;
      }
      h1:first-child,
      h2:first-child,
      h3:first-child,
      h4:first-child {
        margin-top: 0;
      }
      h1 {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(2.75rem, 1.8947368421rem + 3.4210526316vw, 6rem));
        font-weight: 500;
        line-height: 1;
      }
      @media (max-width: 60rem) {
        h1 {
          line-height: 1.2;
        }
      }
      h2 {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(2rem, 1.4078947368rem + 2.3684210526vw, 4.25rem));
        font-weight: 700;
        line-height: 1.1;
      }
      @media (max-width: 60rem) {
        h2 {
          line-height: 1.2;
        }
      }
      h3 {
        font-size: calc(var(--scale-pp) * clamp(1.5rem, 1.1052631579rem + 1.5789473684vw, 3rem));
      }
      h3,
      h4 {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-weight: 700;
        line-height: 1.2;
      }
      h4 {
        font-size: calc(
          var(--scale-pp) * clamp(1.25rem, 1.0197368421rem + 0.9210526316vw, 2.125rem)
        );
      }
      @media (max-width: 60rem) {
        h4 {
          line-height: 1.3;
        }
      }
      :focus {
        outline: thin dotted;
      }
      .wp-block-button .wp-block-button__link {
        align-items: center;
        background: #1a1b31;
        border: 0.125rem solid #1a1b31;
        border-radius: 99rem;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(
          var(--scale-pp) * clamp(0.875rem, 0.7763157895rem + 0.3947368421vw, 1.25rem)
        );
        font-weight: 700;
        letter-spacing: 0.015625em;
        line-height: 1;
        line-height: 0.9;
        padding: calc(var(--scale-pp) * 0.75em) calc(var(--scale-pp) * 1.25em);
        position: relative;
        text-decoration: none;
        transition: all 0.2s;
      }
      .wp-block-button .wp-block-button__link:after {
        background-color: currentColor;
        content: "";
        display: inline-block;
        height: 1em;
        margin-left: calc(var(--scale-pp) * 0.625em);
        -webkit-mask-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxMCAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMS4xMTE2MSAxNC42OTVMOC41NTUwNCA4LjAyODM0TDEuMTExNjIgMS4zNjE2NiIgc3Ryb2tlPSIjMUExQjMxIiBzdHJva2Utd2lkdGg9IjEuNSIvPjwvc3ZnPg==");
        mask-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxMCAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMS4xMTE2MSAxNC42OTVMOC41NTUwNCA4LjAyODM0TDEuMTExNjIgMS4zNjE2NiIgc3Ryb2tlPSIjMUExQjMxIiBzdHJva2Utd2lkdGg9IjEuNSIvPjwvc3ZnPg==");
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-size: auto 0.625em;
        mask-size: auto 0.625em;
        position: relative;
        transform: translateY(-0.0625em);
        transition: all 0.2s;
        vertical-align: middle;
        width: 0.625em;
      }
      .wp-block-button .wp-block-button__link:focus {
        outline: none;
      }
      .wp-block-button .wp-block-button__link:active,
      .wp-block-button .wp-block-button__link:hover {
        background-color: #1a1b31;
        color: #fff;
      }
      .wp-block-button .wp-block-button__link:active:after,
      .wp-block-button .wp-block-button__link:hover:after {
        transform: translateX(3px) translateY(-0.0625em);
      }
      .wp-block-button .wp-block-button__link.has-white-background-color {
        background-color: #fff;
        border-color: #fff;
        color: #0d0f22;
      }
      .wp-block-button.is-style-link .wp-block-button__link {
        background-color: transparent;
        border: 1px solid transparent;
        border-radius: 0;
        color: #0a485f;
        padding: calc(var(--scale-pp) * 0.5625rem) calc(var(--scale-pp) * 0.625rem)
          calc(var(--scale-pp) * 0.5625rem) calc(var(--scale-pp) * 0.5rem);
        text-decoration: none;
      }
      .wp-block-button.is-style-link .wp-block-button__link:after {
        background-color: currentColor;
      }
      .wp-block-button.is-style-link .wp-block-button__link:hover {
        background-color: transparent;
        text-decoration: underline;
      }
      .wp-block-button.is-style-outline .wp-block-button__link {
        background-color: hsla(0, 0%, 100%, 0);
        color: #1a1b31;
      }
      ::placeholder {
        color: #777783 !important;
      }
      iframe,
      img {
        max-width: 100%;
      }
      img {
        height: auto;
        vertical-align: middle;
      }
      table {
        border-collapse: collapse;
        border-spacing: 0;
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(
          var(--scale-pp) * clamp(0.875rem, 0.7763157895rem + 0.3947368421vw, 1.25rem)
        );
        font-weight: 400;
        line-height: 1.5;
        margin: 0 0 calc(var(--scale-pp) * 0.5rem);
        width: 100%;
      }
      @media (max-width: 60rem) {
        table {
          line-height: 1.4;
        }
      }
      tr {
        border-bottom: 1px solid gray;
      }
      td {
        border-bottom: 1px solid #ececec;
        border-top: 1px solid #ececec;
        padding: calc(var(--scale-pp) * 0.625rem);
      }
      @media (max-width: 48rem) {
        td {
          white-space: nowrap;
        }
      }
      ol,
      ul {
        padding-left: calc(var(--scale-pp) * 2.5rem);
      }
      ol li,
      ul li {
        margin-top: calc(var(--scale-pp) * 1em);
        position: relative;
      }
      .content-area {
        position: relative;
      }
      .content-area:after {
        clear: both;
        content: "";
        display: table;
      }
      .content-area > * {
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
        padding-left: var(--container-side-padding);
        padding-right: var(--container-side-padding);
        width: 100%;
      }
      @media (min-width: 62.501rem) {
        .content-area > * {
          width: calc(54.14709vw + 1.68421rem);
        }
      }
      @media only screen and (min-width: 62.75rem) {
        .content-area > * {
          max-width: 62.75rem;
        }
      }
      @media only screen and (min-width: 120rem) {
        .content-area > * {
          max-width: calc(54.75rem + 10vw);
        }
      }
      .content-area > h1:first-child {
        margin-top: var(--space-3xl);
      }
      .content-area > .wp-block-columns {
        max-width: 100%;
        width: 100%;
      }
      @media (min-width: 62.501rem /*1000px*/) {
        .content-area > .wp-block-columns {
          width: calc(78vw + 2rem); // Tweaked by Vincit, was calc(88.22447vw + 1.68421rem);
        }
      }
      @media only screen and (min-width: 100.9166666667rem /*1615px*/) {
        .content-area > .wp-block-columns {
          max-width: 80rem; // Tweaked by Vincit, was 100.9166666667rem;
        }
      }
      @media only screen and (min-width: 120rem /*1920px*/) {
        .content-area > .wp-block-columns {
          max-width: calc(70rem + 10vw); // Tweaked by Vincit, was calc(92.91667rem + 10vw);
        }
      }
      .content-area > ol,
      .content-area > ul {
        padding-left: calc(var(--container-side-padding) + calc(var(--scale-pp) * 2.5rem));
      }
      .content-area--single .entry-meta,
      .content-area--single .entry-title {
        max-width: 100%;
        width: 100%;
      }
      @media (min-width: 62.501rem) {
        .content-area--single .entry-meta,
        .content-area--single .entry-title {
          width: calc(71.18578vw + 1.68421rem);
        }
      }
      @media only screen and (min-width: 81.8333333333rem) {
        .content-area--single .entry-meta,
        .content-area--single .entry-title {
          max-width: 81.8333333333rem;
        }
      }
      @media only screen and (min-width: 120rem) {
        .content-area--single .entry-meta,
        .content-area--single .entry-title {
          max-width: calc(73.83333rem + 10vw);
        }
      }
      .alignfull {
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
        padding-left: 0;
        padding-right: 0;
        width: auto;
      }
      .container {
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
        padding-left: var(--container-side-padding);
        padding-right: var(--container-side-padding);
        width: 100%;
      }
      @media (min-width: 62.501rem /*1000px*/) {
        .container {
          width: calc(78vw + 2rem); // Tweaked by Vincit, was calc(88.22447vw + 1.68421rem);
        }
      }
      @media only screen and (min-width: 100.9166666667rem /*1615px*/) {
        .container {
          max-width: 80rem; // Tweaked by Vincit, was 100.9166666667rem;
        }
      }
      @media only screen and (min-width: 120rem /*1920px*/) {
        .container {
          max-width: calc(70rem + 10vw); // Tweaked by Vincit, was calc(92.91667rem + 10vw);
        }
      }
      body.push-content {
        padding-top: calc(var(--scale-pp) * 5rem) !important;
      }
      @media (max-width: 62.5rem) {
        body.push-content {
          padding-top: calc(var(--scale-pp) * 3.5rem) !important;
        }
      }
      figure,
      ol,
      p,
      table,
      ul {
        margin: calc(var(--scale-pp) * 1.3em) 0 0;
      }
      figure:first-child,
      p:first-child,
      table:first-child {
        margin-top: 0;
      }
      .content-area > * + *,
      .wp-block-column > * + * {
        margin-bottom: 0;
        margin-top: calc(var(--scale-pp) * 1.3em);
      }
      .wp-block-embed,
      .wp-block-table {
        margin-bottom: var(--space-l);
      }
      .content-area > :last-child:not(.has-background):not(.image-and-text) {
        margin-bottom: var(--space-2xl);
      }
      .content-area > h2,
      .content-area > h3 {
        margin-top: calc(var(--scale-pp) * 1.5em);
      }
      .content-row {
        margin-bottom: var(--space-2xl);
        margin-top: var(--space-2xl);
      }
      .content-row.has-background {
        padding-bottom: var(--space-2xl);
        padding-top: var(--space-2xl);
      }
      .wp-block-buttons {
        display: flex;
        flex-wrap: wrap;
        margin-top: calc(var(--scale-pp) * 1.375rem);
      }
      .wp-block-button {
        display: inline-block;
        margin-right: calc(var(--scale-pp) * 0.5rem);
        margin-top: calc(var(--scale-pp) * 0.5rem);
      }
      .wp-block-button:last-child {
        margin-right: 0;
      }
      .wp-block-columns {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: calc(var(--scale-pp) * 1.75em);
      }
      @media (min-width: 60.001rem) {
        .wp-block-columns {
          flex-wrap: nowrap;
        }
      }
      .wp-block-column {
        flex-grow: 1;
        min-width: 0;
      }
      @media (max-width: 60rem) {
        .wp-block-column {
          flex-basis: 100% !important;
        }
      }
      @media (min-width: 60.001rem) {
        .wp-block-column {
          flex-basis: 0;
          flex-grow: 1;
        }
      }
      .wp-block-column.is-vertically-aligned-top {
        align-self: flex-start;
      }
      .wp-block-column.is-vertically-aligned-top {
        width: 100%;
      }
      @media (min-width: 60.001rem) {
        .wp-block-column:not(:first-child) {
          margin-left: var(--space-m);
        }
      }
      @media (max-width: 60rem) {
        .wp-block-column:not(:first-child) {
          margin-top: calc(var(--scale-pp) * 1.3em);
        }
      }
      .wp-has-aspect-ratio .wp-block-embed__wrapper {
        height: 0;
        overflow: hidden;
        padding-top: 56.25%;
        position: relative;
      }
      .wp-has-aspect-ratio .wp-block-embed__wrapper iframe {
        height: 100%;
        left: 0;
        max-width: 100%;
        position: absolute;
        top: 0;
        width: 100%;
      }
      .wp-block-image {
        margin-bottom: var(--space-l);
        margin-top: var(--space-l);
      }
      .wp-block-image img {
        border-radius: 1.25rem;
      }
      .wp-block-image figcaption {
        color: inherit;
        margin-top: calc(var(--scale-pp) * 1.3em);
        text-align: center;
      }
      blockquote {
        margin-bottom: var(--space-l);
        margin-top: var(--space-l);
      }
      .entry-meta {
        align-items: center;
        color: #777783;
        display: flex;
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(0.75rem, 0.6842105263rem + 0.2631578947vw, 1rem));
        font-weight: 700;
        gap: 1.143em;
        line-height: 1.4;
      }
      @media (max-width: 60rem) {
        .entry-meta {
          line-height: 1.5;
        }
      }
      .entry-meta__category {
        align-items: center;
        display: flex;
        gap: inherit;
      }
      /* Tweaked by Vincit, was .entry-meta a */
      .entry-meta .bulletin-category {
        border: 0.0625rem solid #777783;
        border-radius: 3rem;
        color: inherit;
        padding: calc(var(--scale-pp) * 0.429em) calc(var(--scale-pp) * 1.143em);
        text-decoration: none;
      }
      /* Tweaked by Vincit, commented out (category marker is not a button in Partner Portal)
      .entry-meta a:focus {
        outline: 0;
      }
      .entry-meta a:hover {
        background: #1a1b31;
        border-color: #1a1b31;
        color: #f4f4f4;
      }
       */
      .screen-reader-text {
        clip: rect(1px, 1px, 1px, 1px);
        word-wrap: normal !important;
        border: 0;
        -webkit-clip-path: inset(50%);
        clip-path: inset(50%);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        width: 1px;
      }
      .screen-reader-text:focus {
        clip: auto !important;
        background-color: #0a485f;
        -webkit-clip-path: none;
        clip-path: none;
        color: #fff;
        display: block;
        font-size: calc(var(--scale-pp) * 0.875rem);
        font-weight: 700;
        height: auto;
        left: 0.5rem;
        margin: 0;
        padding: calc(var(--scale-pp) * 0.75rem);
        text-decoration: none;
        top: 0.5rem;
        width: auto;
        z-index: 100000;
      }
      @media (prefers-reduced-motion: reduce) {
        *,
        :after,
        :before {
          animation-duration: 1ms !important;
          transition-duration: 1ms !important;
        }
      }
      .has-white-background-color {
        --color-background: #fff;
        background-color: var(--color-background);
      }
      .entry-title {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(2rem, 1.4078947368rem + 2.3684210526vw, 4.25rem));
        font-weight: 700;
        line-height: 1.1;
      }
      @media (max-width: 60rem) {
        .entry-title {
          line-height: 1.2;
        }
      }
      .h4 {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-weight: 700;
        line-height: 1.2;
      }
      .h4 {
        font-size: calc(
          var(--scale-pp) * clamp(1.25rem, 1.0197368421rem + 0.9210526316vw, 2.125rem)
        );
      }
      @media (max-width: 60rem) {
        .h4 {
          line-height: 1.3;
        }
      }
    }
    /*! CSS Used from: Embedded */
    /* Tweaked by Vincit, was body */
    .bulletin-content {
      padding-top: 0px;
      padding-right: 0px;
      padding-bottom: 0px;
      padding-left: 0px;
    }
    a:where(:not(.wp-element-button)) {
      text-decoration: underline;
    }
    .wp-element-button,
    .wp-block-button__link {
      background-color: #32373c;
      border-width: 0;
      color: #fff;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      padding: calc(calc(var(--scale-pp) * 0.667em) + 2px)
        calc(calc(var(--scale-pp) * 1.333em) + 2px);
      text-decoration: none;
    }
    .has-white-background-color {
      background-color: #ffffff !important; // Tweaked by Vincit, was var(--wp--preset--color--white) !important;
    }
    .wp-block-button .wp-block-button__link.has-white-background-color {
      background-color: #ffffff !important; // Tweaked by Vincit, was var(--wp--preset--color--white) !important;
    }
    /*! CSS Used from: Embedded */
    #shadow-indicator {
      font-family: Arial, sans-serif;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      color: #fff;
      background: #cc0000;
      z-index: 3000;
      font-size: calc(var(--scale-pp) * 16px);
      line-height: 1;
      text-align: center;
      padding: 5px;
    }
    #shadow-indicator a.clearlink {
      text-decoration: underline;
      color: #fff;
    }
    /*! CSS Used from: https://www.iloq.com/wp-content/plugins/meomblocks/build/big-quote-styles.css?ver=1678693258 ; media=all */
    @media all {
      .big-quote__blockquote {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(1.5rem, 1.1052631579rem + 1.5789473684vw, 3rem));
        font-weight: 700;
        line-height: 1.2;
        margin: 0 0 var(--space-m);
      }
      @media (min-width: 48.001rem) {
        .big-quote__blockquote > * {
          max-width: 84%;
        }
      }
      .big-quote__author {
        align-items: center;
        display: flex;
      }
      .big-quote__author-content {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(1rem, 0.8684210526rem + 0.5263157895vw, 1.5rem));
        font-weight: 400;
        line-height: 1.5;
      }
      .big-quote__author-content p {
        margin: 0;
      }
      .big-quote__author--image {
        margin-right: 1.125rem;
      }
      .big-quote__author--image img {
        border-radius: 10rem;
        height: 3.875rem;
        -o-object-fit: cover;
        object-fit: cover;
        width: 3.875rem;
      }
      .big-quote__author--title {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(1rem, 0.8684210526rem + 0.5263157895vw, 1.5rem));
        font-weight: 700;
        line-height: 1.1;
      }
    }
    /*! CSS Used from: https://www.iloq.com/wp-content/plugins/meomblocks/build/small-image-and-text-styles.css?ver=1678693258 ; media=all */
    @media all {
      @media (min-width: 60.001rem) {
        .small-image-and-text__container {
          align-items: center;
          display: flex;
          gap: 12.142%;
          justify-content: space-between;
        }
      }
      .small-image-and-text__image {
        border-radius: 1.25rem;
        overflow: hidden;
      }
      .small-image-and-text__image img {
        vertical-align: top;
      }
      .small-image-and-text__image.is-animating img {
        animation: simple-zoom-in-animation 1.3s forwards;
      }
      @media (min-width: 60.001rem) {
        .small-image-and-text__image {
          flex: 0 0 49.643%;
        }
      }
      @media (max-width: 60rem) {
        .small-image-and-text__image {
          margin: 0 auto var(--space-m);
          text-align: center;
        }
      }
      .small-image-and-text__content h2 {
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(1.5rem, 1.1052631579rem + 1.5789473684vw, 3rem));
        font-weight: 700;
        line-height: 1.2;
      }
      @media (min-width: 60.001rem) {
        .small-image-and-text__content {
          flex: 1;
        }
        .small-image-and-text--position-left .small-image-and-text__image {
          order: 1;
          text-align: right;
        }
        .small-image-and-text--position-left .small-image-and-text__content {
          order: 2;
        }
        .small-image-and-text--position-right .small-image-and-text__image {
          order: 2;
          text-align: left;
        }
        .small-image-and-text--position-right .small-image-and-text__content {
          order: 1;
        }
        .small-image-and-text--alignment-top .container {
          align-items: flex-start;
        }
      }
    }
    /*! CSS Used from: https://www.iloq.com/wp-content/plugins/meomblocks/build/image-link-styles.css?ver=1678693258 ; media=all */
    @media all {
      .image-link {
        align-items: flex-end;
        display: flex;
        flex: 1 1 50%;
        min-height: 50vw;
        min-width: min(100%, 28rem);
        position: relative;
      }
      @media (max-width: 48rem) {
        .image-link {
          min-height: 90vw;
        }
      }
      .image-link__image {
        z-index: 1;
      }
      .image-link__image,
      .image-link__image img,
      .image-link__image:before {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }
      .image-link__image img {
        -o-object-fit: cover;
        object-fit: cover;
        z-index: 1;
      }
      .image-link__image:before {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4));
        content: "";
        z-index: 2;
      }
      .image-link__content {
        color: #f4f4f4;
        padding: var(--container-side-padding);
        position: relative;
        z-index: 2;
      }
      @media (min-width: 92.9176666667rem) {
        .image-link__content {
          width: 80%;
        }
      }
      @media (max-width: 60rem) {
        .image-link__content {
          padding: var(--space-l) var(--container-side-padding);
        }
      }
      .image-link__content h2 {
        color: inherit;
        font-family: Avenir, -apple-system, BlinkMacSystemFont, Arial, Helvetica, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: calc(var(--scale-pp) * clamp(1.5rem, 1.1052631579rem + 1.5789473684vw, 3rem));
        font-weight: 700;
        line-height: 1.2;
      }
    }
    /*! CSS Used from: https://www.iloq.com/wp-content/plugins/meomblocks/build/image-links-styles.css?ver=1678693258 ; media=all */
    @media all {
      .image-links {
        display: flex;
        flex-wrap: wrap;
        padding: 0;
      }
      .image-links.has-background.content-row {
        padding: 0;
      }
    }
  }
}
</style>
